import React, { useEffect, useMemo } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { BadgeIndianRupee, ChartColumn, ChartSpline, NotepadText } from "lucide-react";
import useSubscriptionFeatures from "../../hooks/useSubscriptionFeatures";
import { SUB_FEATURES } from "../../constants/subscriptionFeatures";

/**
 * Reports layout — single sidebar entry, nested nav inside.
 *
 * The main app sidebar now points at /reports (no submenu). Once here, the
 * user picks Sales / Performance / Progress from a vertical nav on the left,
 * and the right pane renders the matching child route via <Outlet>. This
 * keeps the global sidebar uncluttered while still allowing deep-link URLs
 * like /reports/sales to load straight into the correct sub-report.
 */
// Each entry declares its plan-flag (or undefined for always-on). Sales and
// Performance are CRM-flavoured; Progress is construction-milestone tracking
// and stays available on both plans.
const SUB_NAV_ALL = [
  {
    label: "Sales",
    to: "/reports/sales",
    icon: BadgeIndianRupee,
    description: "Revenue, bookings, source mix",
    feature: SUB_FEATURES.REPORTS_SALES,
  },
  {
    label: "Performance",
    to: "/reports/performance",
    icon: ChartColumn,
    description: "Sales team & agent leaderboard",
    feature: SUB_FEATURES.REPORTS_SALES,
  },
  {
    label: "Progress",
    to: "/reports/completion",
    icon: ChartSpline,
    description: "Construction milestones",
    feature: SUB_FEATURES.REPORTS_PROGRESS,
  },
];

function Reports() {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasFeature } = useSubscriptionFeatures();

  // Filter the sub-nav to only entries the tenant's plan unlocks. Plus
  // tenants see Progress only; Unified sees everything.
  const SUB_NAV = useMemo(
    () => SUB_NAV_ALL.filter((entry) => !entry.feature || hasFeature(entry.feature)),
    // hasFeature is stable for the same subscription; recompute when plan changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Landing on bare /reports — bounce to the first available sub-report so
  // the right pane is never empty. Plus tenants land on /reports/completion.
  useEffect(() => {
    if (location.pathname === "/reports" || location.pathname === "/reports/") {
      const first = SUB_NAV[0]?.to || "/reports/completion";
      navigate(first, { replace: true });
    }
  }, [location.pathname, navigate, SUB_NAV]);

  return (
    <div className="flex flex-col lg:flex-row min-h-[calc(100vh-4rem)] bg-slate-50/40">
      {/* Left nested sidebar */}
      <aside className="lg:w-64 shrink-0 lg:border-r border-slate-200 bg-white">
        <div className="px-5 py-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
              <NotepadText size={18} className="text-slate-700" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-500">Reports</p>
              <h2 className="text-sm font-semibold text-slate-900">Insights & analytics</h2>
            </div>
          </div>
        </div>
        <nav className="p-3 flex flex-col gap-1">
          {SUB_NAV.map(({ label, to, icon: Icon, description }) => (
            <NavLink
              key={to}
              to={to}
              end={false}
              className={({ isActive }) =>
                `group flex items-start gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                  isActive
                    ? "bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm"
                    : "text-slate-700 hover:bg-slate-50"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      isActive ? "bg-white/20" : "bg-slate-100 group-hover:bg-flamingo-50"
                    }`}
                  >
                    <Icon
                      size={16}
                      className={isActive ? "text-white" : "text-slate-600 group-hover:text-flamingo-600"}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold leading-tight">{label}</p>
                    <p
                      className={`text-[11px] leading-snug mt-0.5 ${
                        isActive ? "text-white/80" : "text-slate-500"
                      }`}
                    >
                      {description}
                    </p>
                  </div>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Right pane — active sub-report renders here */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}

export default Reports;
